/**
 * CSV → unit-record helpers — ported from the Claude Design importer prototype
 * (`mall-analytics-dashboard/project/import-app.jsx`, ALIASES + csvToRecords).
 *
 * Uses Papa Parse (already in deps) instead of the prototype's hand-rolled CSV
 * parser, but keeps the same canonical-field alias map so any of the common
 * column names a leasing team might use map to the same internal record.
 */
import Papa from 'papaparse';

export const FIELD_ALIASES = {
    unit_code: ['unitcode', 'code', 'unit', 'unitid', 'tenantcode', 'tenant_code', 'store_code', 'storecode'],
    tenant_name: ['tenantname', 'storename', 'name', 'store', 'tenant', 'brand'],
    category: ['category', 'type', 'segment', 'dept', 'department'],
    sq_ft: ['sqft', 'squarefootage', 'square_feet', 'area', 'size', 'gla'],
    capacity: ['capacity', 'cap', 'maxoccupancy'],
    footfall: ['footfall', 'visitors', 'traffic', 'visits', 'foottraffic'],
    gross_sales: ['grosssales', 'gross_sales', 'sales', 'revenue', 'todaysales', 'grossrevenue'],
    transactions: ['transactions', 'txns', 'transactioncount', 'salescount', 'orders', 'tickets'],
    atv: ['atv', 'averageticket', 'avgticket', 'basketsize', 'averagebasket', 'averagetransactionvalue'],
    conversion_rate: ['conversionrate', 'conversion', 'conv', 'cr'],
    occupancy: ['occupancy', 'occ', 'currentoccupancy', 'live', 'peopleinside'],
    health_status: ['healthstatus', 'health', 'status', 'state'],
    trend_pct: ['trendpct', 'trend', 'growth', 'change', 'vsyesterday', 'delta'],
    floor: ['floor', 'level', 'storey', 'story'],
    zone: ['zone', 'wing', 'block', 'area_name'],
};

export const slug = (s) => String(s || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

/** Normalize a raw header to one of FIELD_ALIASES keys, or null. */
export function canonicalField(header) {
    const h = String(header).trim().toLowerCase().replace(/[^a-z0-9]/g, '');
    for (const [field, aliases] of Object.entries(FIELD_ALIASES)) {
        if (h === field.replace(/_/g, '') || aliases.includes(h)) return field;
    }
    return null;
}

/**
 * Parse a CSV string into canonical unit records.
 * Returns { records, columns, rawHeaders }; records is filtered to rows with a
 * non-empty unit_code so the caller can safely key by it.
 */
export function csvToRecords(text) {
    const parsed = Papa.parse(text, { skipEmptyLines: true });
    const rows = parsed.data;
    if (!rows.length) return { records: [], columns: [], rawHeaders: [] };

    const headers = rows[0];
    const map = headers.map(canonicalField);
    const records = rows.slice(1).map((r) => {
        const o = {};
        map.forEach((field, i) => { if (field) o[field] = r[i]; });
        return o;
    }).filter((o) => o.unit_code && String(o.unit_code).trim());

    const columns = [...new Set(map.filter(Boolean))];
    return { records, columns, rawHeaders: headers };
}

/** CSV template string for download. */
export function templateCsv() {
    const header = 'unit_code,tenant_name,category,sq_ft,capacity,footfall,gross_sales,transactions,atv,conversion_rate,occupancy,health_status,trend_pct,floor,zone';
    const example = 'A-101,Aurora Apparel,Fashion,6400,160,312,18420,214,86.07,24.6,88,excellent,12.4,1,North';
    return `${header}\n${example}\n`;
}

/** Apply CSV records to existing edit-units, filling tenant_name/sqft/status
 *  by matching on unit_code (slug-normalized). Records without a matching
 *  polygon are returned in `unplaced` so the caller can auto-place them. */
export function mergeCsvIntoEditUnits(editUnits, records) {
    const recByCode = {};
    records.forEach((r) => { recByCode[slug(r.unit_code)] = r; });

    const usedCodes = new Set();
    const merged = editUnits.map((u) => {
        const rec = u.unit_id ? recByCode[slug(u.unit_id)] : null;
        if (!rec) return u;
        usedCodes.add(slug(u.unit_id));
        return {
            ...u,
            tenant_name: rec.tenant_name || u.tenant_name || '',
            tenant_id: u.tenant_id || rec.tenant_id || '',
            zone: rec.zone || u.zone || '',
            floor: Number(rec.floor) || u.floor || 1,
            size_sqft: Number(rec.sq_ft) || u.size_sqft || 0,
            status: rec.health_status || u.status || 'vacant',
        };
    });

    const unplaced = records.filter((r) => !usedCodes.has(slug(r.unit_code)));
    return { merged, unplaced };
}
